import { Plan, useAuthContext } from 'context/AuthContext'

import {
  CAMPAIGN_MODAL_EVENT,
  CampaignModalKind
} from 'utils/plan-upgrade-event'
import { track } from 'utils/tracking'

type UseCreationBlockedReturn = {
  /**
   * true quando o merchant não pode criar recursos novos: plano Free
   * (descontinuado) ou trial já expirado. Leitura, edição, saque e exportação
   * de relatórios seguem liberados.
   */
  creationBlocked: boolean
  /**
   * Abre o modal de upgrade para o recurso informado. Use no lugar do handler
   * original quando `creationBlocked` for true.
   */
  openUpgradeModal: (resourceName: string, plan?: Plan) => void
}

/**
 * Gate de criação por plano (SC-10363).
 *
 * A decisão vem do backend (MerchantCapability#creation_blocked?), transportada
 * no token — o frontend não recalcula a regra.
 */
export const useCreationBlocked = (): UseCreationBlockedReturn => {
  const { creationBlocked, setModalPricing, showUpgradeModal, user } =
    useAuthContext()

  /**
   * Qual modal abrir. Quem está com a conta limitada entra na campanha de fim
   * do Free e vê a oferta que combina com seu perfil — decidido no backend pelo
   * ShowUpgradeModalEvaluator e transportado no token:
   *
   * - free com até 20 cobranças pagas → oferta do Start (ModalNewPlansSmall)
   * - free com mais de 20              → novos planos (ModalNewPlansGeneric)
   * - trial encerrado                  → novos planos, com a copy de período
   *                                      gratuito acabado
   *
   * Fora da campanha (merchant pagante sem acesso a um recurso específico), o
   * comportamento segue o de sempre: o ModalPricing daquele recurso.
   */
  const openUpgradeModal = (resourceName: string, plan: Plan = 'start') => {
    if (creationBlocked) {
      const isTrial = user?.current_plan === 'plan_trial'

      const kind: CampaignModalKind = isTrial
        ? 'trial_ended'
        : showUpgradeModal
        ? 'small'
        : 'generic'

      // SC-10694: este é o ponto onde o usuário bate no limite do plano — o
      // topo do funil de receita, até agora sem nenhum evento. `variant` é o
      // dado mais valioso: as três ofertas abaixo são um teste A/B rodando em
      // produção cujo resultado ninguém conseguia ler.
      track('paywall_blocked', {
        resource_name: resourceName,
        suggested_plan: plan,
        current_plan: user?.current_plan,
        variant: kind
      })
      track('upgrade_modal_viewed', {
        resource_name: resourceName,
        suggested_plan: plan,
        current_plan: user?.current_plan,
        variant: kind
      })

      window.dispatchEvent(
        new CustomEvent(CAMPAIGN_MODAL_EVENT, { detail: { kind } })
      )
      return
    }

    // Merchant pagante sem acesso a um recurso específico: mesmo funil, outra
    // porta (ModalPricing daquele recurso em vez da campanha).
    track('paywall_blocked', {
      resource_name: resourceName,
      suggested_plan: plan,
      current_plan: user?.current_plan,
      variant: 'pricing'
    })
    track('upgrade_modal_viewed', {
      resource_name: resourceName,
      suggested_plan: plan,
      current_plan: user?.current_plan,
      variant: 'pricing'
    })

    setModalPricing({ isOpen: true, plan, resourceName, variant: 'pricing' })
  }

  return { creationBlocked, openUpgradeModal }
}

export default useCreationBlocked
